import { IDropdownActivityProps } from "../../properties/ActivityProperties";
import {
  parseBodyMapFromData,
  parseContentMapFromData,
  parseMaterialMapFromData,
  checkAnswerState,
} from "../../utilization/CatchtivityUtilization";
import { shuffleArray } from "../../utilization/AppUtilization";
import i18n from "../../language/i18n";
import ActivityBodyContent from "./body-contents/ActivityBodyContent";
import InlineSentenceBody, {
  renderInlineParts,
} from "./body-contents/InlineSentenceBody";
import DropdownActivityMaterialContent from "./material-contents/DropdownActivityMaterialContent";
import ActivitySolveLayout from "./ActivitySolveLayout";
import useAnchoredMenu from "../../hooks/useAnchoredMenu";
import { useState, useEffect, useRef } from "react";

const DropdownActivityContent = ({
  answerMap,
  data,
  canAnswerQuestion,
  changeAnswer,
  isPreview,
  showCorrectAnswer,
  isFullScreen,
  passage,
}: IDropdownActivityProps) => {
  const contentMap = parseContentMapFromData(data);
  const dropdownBodyMap = parseBodyMapFromData(data, "DROPDOWN");
  const dropdownMaterialMap = parseMaterialMapFromData(data, "DROPDOWN");
  const [currentAnswerMap, setCurrentAnswerMap] = useState(answerMap);
  // V4 custom popover select — which blank's menu is open (null = none).
  const [openBlankIndex, setOpenBlankIndex] = useState<number | null>(null);
  // Outside click / Escape close it, it flips up near the bottom of the card
  // and scrolls into view — without a wheel-swallowing scrim (see the hook).
  const { anchorRef, menuRef, openUp } = useAnchoredMenu<
    HTMLSpanElement,
    HTMLSpanElement
  >(openBlankIndex, () => setOpenBlankIndex(null));

  useEffect(() => {
    setCurrentAnswerMap(answerMap);
  }, [answerMap]);

  // Options shuffled once PER ACTIVITY (content-keyed — the component instance
  // survives across activities of the same type, so per-mount state would show
  // the previous activity's options).
  const buildOptionsByKey = () => {
    const map: any = {};
    Object.keys(dropdownMaterialMap || {}).forEach((key) => {
      const correctKey = Object.keys(dropdownMaterialMap[key])[0];
      map[key] = shuffleArray([...(dropdownMaterialMap[key][correctKey] || [])]);
    });
    return map;
  };
  const materialMapKey = JSON.stringify(dropdownMaterialMap);
  const [optionsByKey, setOptionsByKey] = useState<any>(buildOptionsByKey);
  const optionsKeyRef = useRef<string>(materialMapKey);
  useEffect(() => {
    if (optionsKeyRef.current === materialMapKey) return;
    optionsKeyRef.current = materialMapKey;
    setOptionsByKey(buildOptionsByKey());
    setOpenBlankIndex(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [materialMapKey]);

  const handleDropdownAnswerOnChange = (key: any, value: string) => {
    if (isPreview) return;
    const next = { ...currentAnswerMap, [key]: value };
    setCurrentAnswerMap(next);
    changeAnswer(next);
  };

  // Media options (IMAGE/VIDEO/AUDIO) keep the original split layout — the designed
  // select only makes sense for text. (User rule: media uses a different design.)
  if (contentMap.type !== "TEXT") {
    return (
      <ActivitySolveLayout
        variant="split"
        isFullScreen={isFullScreen}
        passage={passage}
        body={
          <ActivityBodyContent
            bodyMap={dropdownBodyMap}
            answerMap={
              showCorrectAnswer
                ? Object.keys(dropdownMaterialMap).reduce(
                    (acc: any, key: string) => {
                      acc[key] = Object.keys(dropdownMaterialMap[key])[0];
                      return acc;
                    },
                    {},
                  )
                : currentAnswerMap
            }
            contentMap={contentMap}
            templateType={"DROPDOWN"}
          />
        }
        interaction={
          <DropdownActivityMaterialContent
            uniqueValue={JSON.stringify(data.contentMap)}
            answerMap={answerMap}
            materialMap={dropdownMaterialMap}
            contentMap={contentMap}
            checkCanAnswerQuestion={canAnswerQuestion}
            onChange={(_aMap: any, key: any, value: string) =>
              handleDropdownAnswerOnChange(key, value)
            }
            isPreview={isPreview}
            showCorrectAnswer={showCorrectAnswer}
          />
        }
      />
    );
  }

  // Inline design (V4 AssignmentActivityScreenV4 `aas__inselect`): the selects
  // live IN the sentence — each `@@` blank is a custom glass popover pill. On
  // review the pill recolors in place; a wrong pick — or a blank left unfilled —
  // reveals the correct answer in a stacked "Doğru" chip below.
  const ddKeys = Object.keys(dropdownMaterialMap);
  const renderDropdownBlank = (blankIndex: number) => {
    const key = ddKeys[blankIndex];
    if (key == null) return null;
    const entry = dropdownMaterialMap[key];
    const correctKey = Object.keys(entry)[0];
    const options = optionsByKey[key] || entry[correctKey] || [];
    const rawAnswer = currentAnswerMap?.[key];
    const hasAnswer = !!rawAnswer && rawAnswer !== "DEFAULT_OPTION";
    const editable = canAnswerQuestion() && !isPreview && !showCorrectAnswer;
    // A graded blank with no pick is EMPTY — its own third state, not
    // "ungraded". It used to stay neutral, so the one blank whose answer the
    // learner does not know was the only one that never revealed it. Amber
    // rather than the red of a wrong pick (they did not answer wrongly, they
    // did not answer), and it gets the same "Doğru cevap" chip.
    const state = !isPreview
      ? null
      : hasAnswer
        ? checkAnswerState(correctKey, rawAnswer)
        : "EMPTY";
    const isCorrect = showCorrectAnswer || state === "CORRECT";
    const isWrong = !showCorrectAnswer && state === "INCORRECT";
    const isBlank = !showCorrectAnswer && state === "EMPTY";
    const isOpen = openBlankIndex === blankIndex;

    const shown = showCorrectAnswer
      ? correctKey
      : hasAnswer
        ? rawAnswer
        : null;

    const pill = (
      <span
        className="aas-insel-wrap"
        ref={isOpen ? anchorRef : undefined}
      >
        <button
          type="button"
          className={`aas-insel${hasAnswer ? " is-filled" : ""}${
            isOpen ? " is-open" : ""
          }${isCorrect ? " is-correct" : ""}${isWrong ? " is-wrong" : ""}${
            isBlank ? " is-blank" : ""
          }`}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          onClick={() => {
            if (!editable) return;
            setOpenBlankIndex(isOpen ? null : blankIndex);
          }}
        >
          {shown ? (
            renderInlineParts(shown)
          ) : (
            <span className="aas-insel-ph">{i18n.t("please_select")}</span>
          )}
          {editable ? (
            <span className="aas-insel-caret" aria-hidden="true">
              ▾
            </span>
          ) : null}
        </button>
        {isOpen && editable ? (
          <span
              ref={menuRef}
              className={`aas-insel-menu${openUp ? " is-up" : ""}`}
              role="listbox"
            >
              {options.map((o: string) => (
                <button
                  key={o}
                  type="button"
                  role="option"
                  aria-selected={rawAnswer === o}
                  className={`aas-insel-opt${rawAnswer === o ? " is-on" : ""}`}
                  onClick={() => {
                    handleDropdownAnswerOnChange(key, o);
                    setOpenBlankIndex(null);
                  }}
                >
                  {renderInlineParts(o)}
                </button>
              ))}
            </span>
        ) : null}
      </span>
    );

    // Wrong OR blank graded pill → stacked "Doğru" chip below (V4 aas__corr).
    if (isWrong || isBlank) {
      return (
        <span className="aas-astack">
          {pill}
          <span className="aas-corr">
            <span className="aas-corr-tag">{i18n.t("correct_label")}</span>
            <span className="aas-corr-val">{renderInlineParts(correctKey)}</span>
          </span>
        </span>
      );
    }
    return pill;
  };

  return (
    <ActivitySolveLayout
      variant="inline"
      isFullScreen={isFullScreen}
      passage={passage}
      body={
        <InlineSentenceBody
          bodyMap={dropdownBodyMap}
          renderBlank={renderDropdownBlank}
          totalBlanks={ddKeys.length}
        />
      }
      interaction={null}
    />
  );
};

export default DropdownActivityContent;
